import React, { useCallback, useEffect, useMemo, useState } from 'react'
import message from 'antd/lib/message'
import classnames from 'classnames'

import useAuthState from 'hooks/useAuthState'
import useUpdateUser from 'hooks/useUpdateUser'
import { analytics, form } from 'helpers'
import { valueOrEmptyString } from 'helpers/strings'
import { getSocialHandle } from 'helpers/utils'

import { USER } from 'helpers/constants'
import {
  DATE_GLOBAL_OPTIONS,
  DIET_OPTIONS,
  EDUCATION_OPTIONS,
  ETHNICITY_OPTIONS,
  EXERCISE_OPTIONS,
  HAVE_CHILDREN_OPTIONS,
  HEIGHT_OPTIONS,
  INTEREST_OPTIONS,
  LOOKING_FOR_OPTIONS,
  PET_OPTIONS,
  POLITICS_OPTIONS,
  RELIGION_OPTIONS,
  STAR_SIGN_OPTIONS,
  USER_DATA_LOCATION,
  WANT_CHILDREN_OPTIONS,
  YES_OPTIONS,
} from 'constants/profile'

import { FORM_CONTROL_WIDTH, FORM_RADIUS } from 'components/Profile/constants'

import ProfileInput from 'components/Profile/ProfileInput'
import ProfileLabel from 'components/Profile/ProfileLabel'
import ProfileSection from 'components/Profile/ProfileSection'
import ProfileSelect from 'components/Profile/ProfileSelect'
import ProfileFormGroup from 'components/Profile/ProfileFormGroup'
import ProfileSectionTitle from 'components/Profile/ProfileSectionTitle'
import ProfileGrid from 'components/Profile/ProfileGrid'
import ProfileWrapper from 'components/Profile/ProfileWrapper'
import ProfileMultiSelect from 'components/Profile/ProfileMultiSelect'
import ProfileLocation from 'components/Profile/ProfileLocation'
import ProfileStickyButton from 'components/Profile/ProfileStickyButton'
import ProfileSectionStickyTitle from 'components/Profile/ProfileSectionStickyTitle'
import ProfileImages from './ProfileImages'

const { USER_IMAGES, USER_PROFILE, USER_INTERESTS } = USER_DATA_LOCATION

const hideExistingFields = true

const Profile = () => {
  const [editedUser, setEditedUser] = useState({})
  const [updatedValues, setUpdatedValues] = useState({})
  const { user } = useAuthState()
  const { update } = useUpdateUser()

  const hasChanges = useMemo(() => !!Object.keys(updatedValues).length, [updatedValues])

  const handleChange = (type, name, value) => {
    if (type === USER_IMAGES) {
      if (value.id === 'profile') {
        setUpdatedValues({
          ...updatedValues,
          profileImage: value.image ?? '',
        })
        setEditedUser({ ...editedUser, profileImage: value.image })
      } else {
        const images = editedUser.images.map(image => {
          if (image.id === value.id) {
            return {
              ...image,
              url: value.image,
            }
          }
          return image
        })
        setUpdatedValues({
          ...updatedValues,
          images,
        })
        setEditedUser({ ...editedUser, images })
      }
    } else if (type === USER_PROFILE) {
      setUpdatedValues({
        ...updatedValues,
        profile: {
          ...updatedValues.profile,
          [name]: value || null,
        },
      })
      setEditedUser({
        ...editedUser,
        profile: {
          ...editedUser.profile,
          [name]: value,
        },
      })
    } else if (type === USER_INTERESTS) {
      setUpdatedValues({ ...updatedValues, interests: value })
    } else {
      setUpdatedValues({ ...updatedValues, [name]: value })
      setEditedUser({ ...editedUser, [name]: value })
    }
  }

  const handleInputChange = ({ type, target: { name, value } }) => handleChange(type, name, value)
  const updateUserImg = ({ type, id, image }) => handleChange(type, 'image', { id, image })
  const convertInterestsIntoSelectValues = interests => interests.map(({ interest }) => interest)

  const handleSave = useCallback(
    async e => {
      e.preventDefault()
      if (!form.checkValidity(e)) {
        return
      }

      if (Object.keys(updatedValues).length) {
        message.loading('saving...', 0)
        try {
          await update(updatedValues)
          message.destroy()
          message.success('Profile Saved', 2.5)
          const { profileImage, images, ...rest } = updatedValues
          analytics.segmentTrack('User Updated Profile', {
            ...rest,
            profileImage: !!profileImage,
            images: !!images?.length,
          })
          setUpdatedValues({})
        } catch (error) {
          message.destroy()
          message.error(error.message || 'Please try again')
        }
      } else {
        message.info('No changes provided for update!')
      }
    },
    [update, updatedValues]
  )

  useEffect(() => {
    if (user) {
      const interests = convertInterestsIntoSelectValues(user.interests)
      setEditedUser({
        ...user,
        profileImage: user.profileImage || USER.PROFILE_IMG.DEFAULT,
        interests,
      })
    }
  }, [user])

  const handleUserProfileInputChange = ({ target }) =>
    handleInputChange({ type: USER_PROFILE, target })

  const handleSocialHandle = ({ target: { name, value } }) => {
    let link = ''
    value = (value || '').replace('@', '')

    if (name === 'instagramLink') {
      link = `https://instagram.com/${value}`
    } else if (name === 'twitterLink') {
      link = `https://twitter.com/${value}`
    }
    handleChange('', name, link)
  }

  return (
    <form noValidate onSubmit={handleSave}>
      <ProfileWrapper user={editedUser?.id ? editedUser : user}>
        <ProfileSectionStickyTitle hideBorder={true} hasChanges={hasChanges}>
          Edit Application
        </ProfileSectionStickyTitle>
        <div className="mb-10">
          <ProfileImages user={editedUser} onImageUpdate={updateUserImg} />
        </div>
        {hasChanges && <ProfileStickyButton type="submit">Save Profile</ProfileStickyButton>}
        <ProfileSection>
          <ProfileSectionTitle>Personal Info</ProfileSectionTitle>
          <ProfileGrid>
            <ProfileFormGroup>
              <ProfileLabel>OCCUPATION</ProfileLabel>
              <ProfileInput
                showRequired
                placeholder="Role"
                value={valueOrEmptyString(editedUser.profile?.occupationPosition)}
                type="text"
                name="occupationPosition"
                onChange={handleUserProfileInputChange}
              />
            </ProfileFormGroup>
            <ProfileFormGroup className="md:self-end">
              <ProfileInput
                showRequired
                placeholder="Company"
                value={valueOrEmptyString(editedUser.profile?.company)}
                type="text"
                name="company"
                onChange={handleUserProfileInputChange}
              />
            </ProfileFormGroup>
            <ProfileFormGroup>
              <ProfileInput
                showRequired
                placeholder="Industry"
                value={valueOrEmptyString(editedUser.profile?.occupation)}
                type="text"
                name="occupation"
                onChange={handleUserProfileInputChange}
              />
            </ProfileFormGroup>
            <div />
            <ProfileFormGroup>
              <ProfileLabel>MY EDUCATION</ProfileLabel>
              <ProfileInput
                showRequired
                type="text"
                placeholder="School / Institution"
                value={valueOrEmptyString(editedUser.profile?.education)}
                name="education"
                onChange={handleUserProfileInputChange}
              />
            </ProfileFormGroup>
            <ProfileFormGroup className="md:self-end">
              <ProfileSelect
                showRequired
                name="educationLevel"
                placeholder="Degree / Diploma"
                value={valueOrEmptyString(editedUser.profile?.educationLevel)}
                options={EDUCATION_OPTIONS}
                onChange={handleUserProfileInputChange}
              />
            </ProfileFormGroup>
          </ProfileGrid>
        </ProfileSection>
        <ProfileSection>
          <ProfileSectionTitle />
          <ProfileGrid>
            <ProfileFormGroup>
              <ProfileLabel>Pronouns</ProfileLabel>
              <ProfileInput
                placeholder="Pronouns"
                value={valueOrEmptyString(editedUser.profile?.pronoun)}
                type="text"
                name="pronoun"
                onChange={handleUserProfileInputChange}
              />
            </ProfileFormGroup>
            <ProfileFormGroup>
              <ProfileLabel>HEIGHT</ProfileLabel>
              <ProfileSelect
                name="height"
                options={HEIGHT_OPTIONS}
                value={valueOrEmptyString(editedUser.profile?.height)}
                onChange={handleUserProfileInputChange}
              />
            </ProfileFormGroup>
            <ProfileFormGroup>
              <ProfileLabel>RELIGION</ProfileLabel>
              <ProfileSelect
                name="religion"
                placeholder="Religion"
                options={RELIGION_OPTIONS}
                value={valueOrEmptyString(editedUser.profile?.religion)}
                onChange={handleUserProfileInputChange}
              />
            </ProfileFormGroup>
            <ProfileFormGroup>
              <ProfileLabel>Political View</ProfileLabel>
              <ProfileSelect
                name="politics"
                value={valueOrEmptyString(editedUser.profile?.politics)}
                options={POLITICS_OPTIONS}
                onChange={handleUserProfileInputChange}
              />
            </ProfileFormGroup>
            <ProfileFormGroup>
              <ProfileLabel>STAR SIGN</ProfileLabel>
              <ProfileSelect
                name="starSign"
                options={STAR_SIGN_OPTIONS}
                value={valueOrEmptyString(editedUser.profile?.starSign)}
                onChange={handleUserProfileInputChange}
              />
            </ProfileFormGroup>
          </ProfileGrid>
        </ProfileSection>
        <ProfileSection>
          <ProfileSectionTitle>Social</ProfileSectionTitle>
          <ProfileGrid>
            <ProfileFormGroup>
              <ProfileLabel>Instagram</ProfileLabel>
              <ProfileInput
                name="instagramLink"
                placeholder="@instagram"
                Input={ProfileInput}
                defaultValue={getSocialHandle(editedUser.instagramLink)}
                onChange={handleSocialHandle}
              />
            </ProfileFormGroup>
          </ProfileGrid>
        </ProfileSection>
        {!hideExistingFields && (
          <ProfileSection>
            <ProfileSectionTitle>Additional</ProfileSectionTitle>
            <ProfileGrid>
              <ProfileFormGroup>
                <ProfileLabel>RELIGION</ProfileLabel>
                <ProfileSelect
                  name="religion"
                  placeholder="Religion"
                  options={RELIGION_OPTIONS}
                  value={valueOrEmptyString(editedUser.profile?.religion)}
                  onChange={handleUserProfileInputChange}
                />
              </ProfileFormGroup>
              <ProfileFormGroup>
                <ProfileLabel>ETHNICITY</ProfileLabel>
                <ProfileSelect
                  name="ethnicity"
                  options={ETHNICITY_OPTIONS}
                  value={valueOrEmptyString(editedUser.profile?.ethnicity)}
                  onChange={handleUserProfileInputChange}
                />
              </ProfileFormGroup>
              <ProfileFormGroup>
                <ProfileLabel>HOMETOWN</ProfileLabel>
                <ProfileLocation
                  initialLocation={editedUser.profile?.birthplace || 'Hometown'}
                  onLocationUpdate={({ location }) => {
                    handleChange(USER_PROFILE, 'birthplace', location)
                  }}
                />
              </ProfileFormGroup>

              <ProfileFormGroup>
                <ProfileLabel>DIET</ProfileLabel>
                <ProfileSelect
                  name="diet"
                  value={valueOrEmptyString(editedUser.profile?.diet)}
                  options={DIET_OPTIONS}
                  onChange={handleUserProfileInputChange}
                />
              </ProfileFormGroup>
              <ProfileFormGroup>
                <ProfileLabel>MY PETS</ProfileLabel>
                <ProfileSelect
                  name="pets"
                  value={valueOrEmptyString(editedUser.profile?.pets)}
                  options={PET_OPTIONS}
                  onChange={handleUserProfileInputChange}
                />
              </ProfileFormGroup>
              <ProfileFormGroup>
                <ProfileLabel>Exercise Frequency</ProfileLabel>
                <ProfileSelect
                  name="exercise"
                  value={valueOrEmptyString(editedUser.profile?.exercise)}
                  options={EXERCISE_OPTIONS}
                  onChange={handleUserProfileInputChange}
                />
              </ProfileFormGroup>

              <ProfileFormGroup>
                <ProfileLabel>PASSION/INTERESTS</ProfileLabel>
                <ProfileMultiSelect
                  className={classnames(FORM_CONTROL_WIDTH, FORM_RADIUS)}
                  name="interests"
                  options={INTEREST_OPTIONS}
                  value={editedUser.interests}
                  onChange={({ name, values }) => {
                    const selectedValues = values.map(({ value }) => value)
                    setEditedUser({ ...editedUser, interests: selectedValues })
                    handleChange(USER_INTERESTS, name, selectedValues)
                  }}
                />
              </ProfileFormGroup>
              <ProfileFormGroup>
                <ProfileLabel>MY CHILDREN</ProfileLabel>
                <ProfileSelect
                  name="haveChildren"
                  value={valueOrEmptyString(editedUser.profile?.haveChildren)}
                  options={HAVE_CHILDREN_OPTIONS}
                  onChange={handleUserProfileInputChange}
                />
              </ProfileFormGroup>
              <ProfileFormGroup>
                <ProfileLabel>WANT CHILDREN?</ProfileLabel>
                <ProfileSelect
                  name="wantChildren"
                  value={valueOrEmptyString(editedUser.profile?.wantChildren)}
                  options={WANT_CHILDREN_OPTIONS}
                  onChange={handleUserProfileInputChange}
                />
              </ProfileFormGroup>
              <ProfileFormGroup>
                <ProfileLabel>DO YOU ENJOY DRINKING?</ProfileLabel>
                <ProfileSelect
                  name="alcohol"
                  value={valueOrEmptyString(editedUser.profile?.alcohol)}
                  options={YES_OPTIONS}
                  onChange={handleUserProfileInputChange}
                />
              </ProfileFormGroup>
              <ProfileFormGroup>
                <ProfileLabel>Do You Enjoy Cannabis?</ProfileLabel>
                <ProfileSelect
                  name="cannabis"
                  value={valueOrEmptyString(editedUser.profile?.cannabis)}
                  options={YES_OPTIONS}
                  onChange={handleUserProfileInputChange}
                />
              </ProfileFormGroup>
              <ProfileFormGroup>
                <ProfileLabel>Do You Enjoy Smoking?</ProfileLabel>
                <ProfileSelect
                  name="smoking"
                  value={valueOrEmptyString(editedUser.profile?.smoking)}
                  options={YES_OPTIONS}
                  onChange={handleUserProfileInputChange}
                />
              </ProfileFormGroup>
              <ProfileFormGroup>
                <ProfileLabel>Do You Enjoy Drugs?</ProfileLabel>
                <ProfileSelect
                  name="drugs"
                  value={valueOrEmptyString(editedUser.profile?.drugs)}
                  options={YES_OPTIONS}
                  onChange={handleUserProfileInputChange}
                />
              </ProfileFormGroup>
              <ProfileFormGroup>
                <ProfileLabel>LOOKING FOR</ProfileLabel>
                <ProfileSelect
                  name="desiredRelationship"
                  value={valueOrEmptyString(editedUser.profile?.desiredRelationship)}
                  options={LOOKING_FOR_OPTIONS}
                  onChange={handleUserProfileInputChange}
                />
              </ProfileFormGroup>
              <ProfileFormGroup>
                <ProfileLabel>Would you date globalLy?</ProfileLabel>
                <ProfileSelect
                  name="wantGlobalDating"
                  value={valueOrEmptyString(editedUser.profile?.wantGlobalDating)}
                  options={DATE_GLOBAL_OPTIONS}
                  onChange={handleUserProfileInputChange}
                />
              </ProfileFormGroup>
            </ProfileGrid>
          </ProfileSection>
        )}
      </ProfileWrapper>
    </form>
  )
}

export default Profile
